import { dumpPrettyText, load } from "ion-js";
import {
  CanonicalBusinessEvent,
  ErrorDetails,
  ErrorRecord,
  FullErrorRecord,
} from "../types";

export function stripExceptionClassName(message: string): string {
  const prefix =
    /^(?:[A-Za-z_$][\w$]*\.)+[A-Za-z_$][\w$]*(?:Exception|Error|Throwable):\s+/;
  let out = message.trim();
  while (prefix.test(out)) {
    out = out.replace(prefix, "").trim();
  }
  return out;
}

export function summarizeHerdStackTrace(
  stackTrace: string | undefined
): string | undefined {
  if (!stackTrace) return undefined;
  const lines = stackTrace.split("\n");
  let lastCausedByIdx = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].trimStart().startsWith("Caused by:")) {
      lastCausedByIdx = i;
    }
  }
  if (lastCausedByIdx === -1) return undefined;

  const parts: string[] = [];
  for (let i = lastCausedByIdx; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (i > lastCausedByIdx) {
      if (
        trimmed.startsWith("at ") ||
        /^\.\.\.\s*\d+\s*more$/.test(trimmed) ||
        trimmed === ""
      ) {
        break;
      }
    }
    parts.push(lines[i].replace(/^Caused by:\s*/, "").trim());
  }
  const message = stripExceptionClassName(parts.join(" ").trim());
  return message || undefined;
}

function formatErrorMessage(errors: ErrorRecord): string {
  const groupedErrors: { [key: string]: Set<string> } = {};

  for (const error of Object.values(errors) as unknown as FullErrorRecord[]) {
    switch (error.error_type) {
      case "credit_debit_unbalance_error": {
        (groupedErrors[error.error_type] ??= new Set()).add(
          error.error_message
        );
        break;
      }
      case "attribute_derivation_error": {
        const [header, details] = error.error_message.split(". ");
        const formattedDetails = details
          .replace(/\[(.*?)\]/g, "\n\t\t$1\n\t ")
          .replace("::", "\n\t\t")
          .replaceAll("{", "")
          .replaceAll("},", ", ");
        (groupedErrors[header] ??= new Set()).add(formattedDetails);
        break;
      }
      default: {
        groupedErrors[error.error_message] ??= new Set();
        break;
      }
    }
  }
  const formattedErrors: string[] = [];
  for (const [errorType, details] of Object.entries(groupedErrors)) {
    if (details.size === 0) {
      formattedErrors.push(errorType);
    } else {
      formattedErrors.push(`${errorType}:`);
      details.forEach((detail) => {
        formattedErrors.push(`\t- ${detail}`);
      });
    }
  }

  return formattedErrors.join("\n");
}

export function parseErrorDetails(
  stackTrace: string,
  destination: string,
  cbe: CanonicalBusinessEvent
): ErrorDetails {
  try {
    // Herd failures are not Ion
    if (cbe.herdFailed) {
      return {
        errorType: "Failed in Herd",
        displayMessage: stackTrace,
        stackTrace,
      };
    }

    if (destination === "FLARE") {
      const ionMatch = stackTrace.match(/{[\s\S]*}/);
      if (ionMatch) {
        const ionData = load(ionMatch[0]);
        const rawErrorDetails = dumpPrettyText(ionData?.get("errors"));
        const inputRequestUrl =
          ionData?.get("input_request_url")?.stringValue() || undefined;
        const errorType =
          ionData?.get("type")?.stringValue() || "Unknown Error";

        const parsedErrorDetails: ErrorRecord | undefined = ionData?.get(
          "errors"
        )
          ? (JSON.parse(JSON.stringify(ionData.get("errors"))) as ErrorRecord)
          : undefined;

        const displayMessage = parsedErrorDetails
          ? formatErrorMessage(parsedErrorDetails)
          : stackTrace;

        const errorRecords: ErrorRecord[] | undefined = ionData?.get(
          "errored_records"
        )
          ? (JSON.parse(
              JSON.stringify(ionData.get("errored_records"))
            ) as ErrorRecord[])
          : undefined;

        return {
          errorType,
          displayMessage,
          rawErrorDetails,
          inputRequestUrl,
          parsedErrorDetails,
          errorRecords,
          stackTrace,
        };
      }
    }

    // Fallback for rACE errors
    const errorTypeMatch = stackTrace.match(/^([\w.]+Exception):/);
    const errorType = errorTypeMatch ? errorTypeMatch[1] : "Unknown Error";
    const errorRecordMatch = stackTrace.match(
      /.*ignored_record_ids:\[((?:"\d+"(?:,\s*)?)+)\]/
    );
    const errorRecordIds = errorRecordMatch
      ? errorRecordMatch[1].split(",").map((id) => id.replace(/"/g, "").trim())
      : [];
    const errorRecords: ErrorRecord[] | undefined =
      errorRecordIds.length > 0
        ? errorRecordIds
            .map((id) => Number(id))
            .map(
              (id) => JSON.parse(JSON.stringify(cbe.records[id])) as ErrorRecord
            )
        : undefined;

    return {
      errorType,
      displayMessage: stackTrace,
      stackTrace,
      errorRecords,
    };
  } catch (error) {
    console.error("Error parsing error details:", error);
    return {
      errorType: "Parsing Error",
      displayMessage: stackTrace,
      stackTrace,
    };
  }
}

export const BADGE_COLORS = [
  "blue",
  "grey",
  "green",
  "red",
  "severity-critical",
  "severity-high",
  "severity-medium",
  "severity-low",
  "severity-neutral",
] as const;

type BadgeColor = (typeof BADGE_COLORS)[number];

export function getBadgeColour(errorType: string): BadgeColor {
  const index =
    errorType.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    BADGE_COLORS.length;
  return BADGE_COLORS[index];
}
